import { Badge, Button, Divider, Grid, Group, Loader, LoadingOverlay, Menu, Paper, Text, Title, UnstyledButton, useMantineTheme } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import moment from 'moment';
import { default as React } from 'react';
import { FaArrowCircleDown, FaCheck, FaEye, FaInfoCircle, FaRegArrowAltCircleRight } from 'react-icons/fa';
import { useApp } from '../../contexts/app.context';
import { GET_NOTIFICATIONS, READ_NOTIFICATIONS } from '../../services/notifications';
import { groupBy } from '../../utility/util';
import InfiniteLoading from '../infinite_loading';
import { BadgeOrigin } from '../theme_paper';
import ReactHtmlParser from 'react-html-parser';

const COLUMNS = [
    {title: "Título", key: 'title'}
]

interface ListProps{}
interface ListHandle{
    refresh: () => void;
}

const List : React.ForwardRefRenderFunction<ListHandle, ListProps> = ({}, ref) => {
    const [filters, setFilters] = React.useState({
        read: '-1',
        following: '-1',
    })
    const [loading, setLoading] = React.useState({});
    const [ending, setEnding] = React.useState({});
    const [loadingPage, setLoadingPage] = React.useState({});
    const [readTemp, setReadTemp] = React.useState({});
    const [data, setData] = React.useState({});
    const [page, setPage] = React.useState({});
    

    const keySearch = [
        `FOLLOWING`,
        `${filters.following}`,
        `READ`,
        `${filters.read}`,
        `KEYSEARCH`,
    ].join(";")

    const shownItems = data[keySearch] || [];
    
    const theme = useMantineTheme();

    const { openTheme } = useApp();

    const readNotifications = (items: {id: string; read: boolean;}[]) => {
        const old = data;
        let newData = {};
        for(let it of items){ newData[`${it.id}`] = it.read }
        setReadTemp(rt => ({...rt, ...newData}))
        
        READ_NOTIFICATIONS(items)
        .then(() => {})
        .catch(err => {
            setData(old);
            showNotification({title: "Ops", message: err.message})
        })
    }

    const loadNotifications = () => {
        const p = page[keySearch] ? page[keySearch] : 0;
        const ksPage = `${keySearch};PAGE;${p}`
        if(!ending[keySearch]){
            setLoading(l => ({...l, [ksPage]: true}));
            setLoadingPage(l => ({...l, [keySearch]: true}));

            GET_NOTIFICATIONS({ page: p, ...filters })
            .then(({notifications}) => {
                setData(d => ({ 
                    ...d, 
                    [keySearch]: groupBy([
                        ...((d && d[keySearch]) ? d[keySearch] : []),
                        ...notifications
                    ], item => item.id).map(g => g[1][0])
                }));
                setLoading(l => ({...l, [ksPage]: false}));
                setLoadingPage(l => ({...l, [keySearch]: false}));
                if(notifications.length === 0) setEnding(e => ({...e, [keySearch]: true}));
            })
            .catch(err => {
                setLoading(l => ({...l, [ksPage]: false}));
                setLoadingPage(l => ({...l, [keySearch]: false}));
                showNotification({title: "Ops.", message: err.message});
            })
        }
    }

    const refresh = () => { loadNotifications(); }

    React.useEffect(loadNotifications, [page, filters]);

    React.useImperativeHandle(ref, () => ({
        refresh: () => { refresh(); }
    }));

    const NotificationPaper = (({title, body, id, read, theme: th}) => <>
        <Paper shadow="sm" p="lg" m="md">
            <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center'}}>
                <div style={{flex: 1}}>
                    <Text weight="bold">{title}</Text>
                    <Text mt="sm">{ReactHtmlParser(body)}</Text>
                </div>
            </div>
            <Divider mt="lg" mb="lg" />
            <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center'}}>
                <div style={{flex: 1}}>
                    <Text weight="bold">{th.title}</Text>
                    <Text weight="bold">{th.follow}</Text>
                </div>
                <BadgeOrigin origin={th.origin} />
                {th.originUrl && <UnstyledButton ml="xs" onClick={() => window.open(th.originUrl, "_blank")}><FaRegArrowAltCircleRight color="gray" /></UnstyledButton>}
            </div>
            <div style={{padding: '20px 0'}}>
                <Text>{th.description}</Text>
            </div>
            <Divider />
            <div style={{padding: '10px 0', display: 'flex', flexDirection: 'row'}}>
                <div style={{flex: 1, paddingRight: 10}}>
                    <Text color={theme.colors.gray[6]}>{th.status?.title}{th.status?.description && ` - ${th.status?.description}`}</Text>
                </div>
                <Text color={theme.colors.gray[6]}>{moment(th.status?.created_at).format("DD/MM/YYYY HH:mm")}</Text>
            </div>
            <Divider />
            <Grid style={{padding: '10px 0', display: 'flex', flexDirection: 'row', justifyContent: 'flex-end'}}>
                <Grid.Col sm={12} md={6}>
                    <Button
                        onClick={() => openTheme(th)}
                        variant="subtle"
                        leftIcon={<FaInfoCircle />}
                        fullWidth
                    >Mais detalhes</Button>
                </Grid.Col>
                <Grid.Col sm={12} md={6}>
                    <Button
                        onClick={() => readNotifications([{id, read: !(readTemp[`${id}`] !== undefined ? readTemp[`${id}`] : read)}])}
                        variant={(readTemp[`${id}`] !== undefined ? readTemp[`${id}`] : read) ? "outline" : "filled"}
                        color={(readTemp[`${id}`] !== undefined ? readTemp[`${id}`] : read) ? "gray" : "yellow"}
                        leftIcon={<FaEye />}
                        fullWidth
                    >{(readTemp[`${id}`] !== undefined ? readTemp[`${id}`] : read) ? "Lido" : "Não lido"}</Button>
                </Grid.Col>
            </Grid>
        </Paper>
    </>)

    return <>
        <Grid m="md" mb="lg">
            <Grid.Col xs={12}>
                <Title order={5}>Status</Title>
            </Grid.Col>
            <Grid.Col xs={12} md={4}>
                <Button
                    fullWidth
                    onClick={() => setFilters(f => ({...f, read: '-1'}))}
                    variant={filters.read === '-1' ? "filled" : "outline"}
                    color={filters.read === '-1' ? "green" : "gray"}
                    leftIcon={filters.read === '-1' ? <FaCheck /> : null}
                >Todas</Button>
            </Grid.Col>
            <Grid.Col xs={12} md={4}>
                <Button
                    fullWidth
                    onClick={() => setFilters(f => ({...f, read: '1'}))}
                    variant={filters.read === '1' ? "filled" : "outline"}
                    color={filters.read === '1' ? "green" : "gray"}
                    leftIcon={filters.read === '1' ? <FaCheck /> : null}
                >Lidas</Button>
            </Grid.Col>
            <Grid.Col xs={12} md={4}>
                <Button
                    fullWidth
                    onClick={() => setFilters(f => ({...f, read: '0'}))}
                    variant={filters.read === '0' ? "filled" : "outline"}
                    color={filters.read === '0' ? "green" : "gray"}
                    leftIcon={filters.read === '0' ? <FaCheck /> : null}
                >Não lidas</Button>
            </Grid.Col>
            <Grid.Col xs={12}>
                <Title order={5}>Interesse</Title>
            </Grid.Col>
            <Grid.Col xs={12} md={4}>
                <Button
                    fullWidth
                    onClick={() => setFilters(f => ({...f, following: '-1'}))}
                    variant={filters.following === '-1' ? "filled" : "outline"}
                    color={filters.following === '-1' ? "green" : "gray"}
                    leftIcon={filters.following === '-1' ? <FaCheck /> : null}
                >Todas</Button>
            </Grid.Col>
            <Grid.Col xs={12} md={4}>
                <Button
                    fullWidth
                    onClick={() => setFilters(f => ({...f, following: '1'}))}
                    variant={filters.following === '1' ? "filled" : "outline"}
                    color={filters.following === '1' ? "green" : "gray"}
                    leftIcon={filters.following === '1' ? <FaCheck /> : null}
                >Acompanhando</Button>
            </Grid.Col>
            {/* <Grid.Col xs={12}>
                <Title order={5}>Opções</Title>
            </Grid.Col>
            <Grid.Col xs={12} md={4}>
                <Menu>
                    <Menu.Target>
                        <Button
                            fullWidth
                            variant="outline"
                            color="gray"
                            leftIcon={<FaArrowCircleDown />}
                        >Opções</Button>
                    </Menu.Target>
                    <Menu.Dropdown>
                        <Menu.Label>Opções</Menu.Label>
                        <Menu.Item onClick={() => { readNotifications(shownItems.map(item => ({id: item.id, read: true}))) }}>
                            Marcar todas lidas
                        </Menu.Item>
                        <Menu.Item onClick={() => { readNotifications(shownItems.map(item => ({id: item.id, read: false}))) }}>
                            Marcar todas não lidas
                        </Menu.Item>
                    </Menu.Dropdown>
                </Menu>
            </Grid.Col> */}
        </Grid>

        <InfiniteLoading
            identifier={keySearch}
            setPage={ps => setPage(p => ({...p, [keySearch]: ps}))}
            items={shownItems}
            render={(item) => <NotificationPaper {...item} />}
        />

        {loadingPage[keySearch] && <div style={{textAlign: 'center'}}><Loader /></div>}

        {!loading && shownItems.length === 0 && <div style={{textAlign: 'center'}}>
            <Text color="gray" mt="lg">Nenhuma notificação para o filtro selecionado</Text>
        </div>}
    </>
}

export default React.forwardRef(List);